import { PASSPORT_BASE_URL } from '@/constant/apiList';
import { getNestedDataFetcher } from '@/utils/apis';

/** GET /api/v1/passport/{id} 중 결재라인 표시에 쓰는 필드만 */
type PassportStatusResponse = {
  passportId: string;
  /** 1=재직, 0=퇴사 */
  activated: number;
  detail?: {
    /** 오늘이 ERP 휴직 발령 기간에 포함되면 true */
    isOnLeave?: boolean;
    /** 휴직 종류 코드 — 휴직이 아니면 null */
    leaveType?: string | null;
    /** 휴직 종류 명칭(요청 locale 기준) — 휴직이 아니면 null */
    leaveTypeName?: string | null;
    divisionCode?: string;
    departmentCode?: string;
    email?: string;
  };
};

/** 소속 판정용 사업장·부서 코드. organizationCode 는 환경(dev/운영)마다 달라 쓰지 않음 */
export type MemberOrganization = {
  divisionCode: string;
  departmentCode: string;
  /** 레거시 USERID 예외 판정용(메일 아이디 = 인트라넷 ID) */
  email?: string;
  /** 조회 응답 결재자 인트라넷 ID — 조회 화면 상신은 email 이 없어 이 값으로 판정 */
  intranetId?: string;
};

/** 결재라인에서 사람 한 명의 상태 판정 결과 */
export type PassportMemberStatus = {
  deactivated: boolean;
  onLeave: boolean;
  /** 휴직 종류 명칭 — 배지 문구에는 쓰지 않고 필요할 때 참고용으로 남겨둔다 */
  leaveTypeName: string | null;
  organization: MemberOrganization | null;
};

const DEACTIVATED_STATUS: PassportMemberStatus = {
  deactivated: true,
  onLeave: false,
  leaveTypeName: null,
  organization: null,
};

// 재직/휴직 여부는 세션 중에 바뀔 일이 거의 없으므로 한 번 확인한 사람은 여기 캐시해 두고,
// 결재라인 멤버가 바뀌어도 새로 들어온 사람만 서버에 물어본다.
const statusCache = new Map<string, PassportMemberStatus>();
// 같은 사람을 여러 화면(결재라인/참조인/상신 가드)이 동시에 물어보면 요청이 겹치므로,
// 응답 도착 전까지는 진행 중인 promise 를 돌려써서 호출을 한 번으로 모은다.
const inFlight = new Map<string, Promise<PassportMemberStatus>>();

const toMemberOrganization = (
  detail: PassportStatusResponse['detail'],
): MemberOrganization | null =>
  detail?.divisionCode && detail.departmentCode
    ? {
        divisionCode: detail.divisionCode,
        departmentCode: detail.departmentCode,
        email: detail.email,
      }
    : null;

/**
 * 퇴사 여부와 휴직 여부를 passport 상세 응답 한 번으로 함께 판정한다.
 * 두 정보가 같은 응답에 들어 있어 휴직 표시를 위한 추가 호출은 필요 없다.
 */
export const fetchPassportMemberStatus = (
  passportId: string,
): Promise<PassportMemberStatus> => {
  const cached = statusCache.get(passportId);
  if (cached !== undefined) return Promise.resolve(cached);

  const pending = inFlight.get(passportId);
  if (pending !== undefined) return pending;

  const request = getNestedDataFetcher<PassportStatusResponse>(
    `${PASSPORT_BASE_URL}/${passportId}`,
  )
    .then((passport) => {
      const status: PassportMemberStatus = {
        deactivated: passport.activated !== 1,
        onLeave: passport.detail?.isOnLeave === true,
        leaveTypeName: passport.detail?.leaveTypeName ?? null,
        organization: toMemberOrganization(passport.detail),
      };
      statusCache.set(passportId, status);
      return status;
    })
    .catch((e) => {
      // 프로필이 404 면 조회 자체가 안 되는 사람이므로 퇴사로 취급한다.
      if ((e as { response?: { status?: number } })?.response?.status === 404) {
        statusCache.set(passportId, DEACTIVATED_STATUS);
        return DEACTIVATED_STATUS;
      }
      // 그 외 오류는 상태를 알 수 없는 것 — 여기서 삼키면 근거 없이 상신 가드를
      // 통과하므로 throw 해서 SWR error 로 올린다.
      throw e;
    })
    .finally(() => {
      inFlight.delete(passportId);
    });

  inFlight.set(passportId, request);
  return request;
};
